import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Sparkles, MessageSquare, Zap, Shield, ArrowRight } from 'lucide-react';
import { DemoAgentForm } from '../components/DemoAgentForm';
import { ReactivationCalculator } from '../components/ReactivationCalculator';

// Textos traducidos para la página
const PAGE_TEXTS = {
  es: {
    freeDemo: 'DEMO GRATUITA',
    heroTitle: <>Prueba el <span className="text-amber-400">Agente IA de Reactivación</span><br />en tu propio teléfono</>,
    heroSub: 'En 30 segundos recibirás un SMS de nuestro agente. Experimenta cómo reactivaríamos a tus clientes inactivos.',
    feature1Title: 'Conversación Natural',
    feature1Desc: 'La IA conversa como una persona real, sin scripts robóticos',
    feature2Title: 'Resultados Inmediatos',
    feature2Desc: 'Genera ventas en días, no en meses',
    feature3Title: 'Respeto Total',
    feature3Desc: 'El agente se detiene inmediatamente si el cliente no está interesado',
    step1Title: 'Completa el Formulario',
    step1Desc: 'Cuéntanos sobre tu negocio y qué oferta quieres promocionar',
    step2Title: 'Recibe un SMS',
    step2Desc: 'Nuestro agente Sara te contactará como si fueras un cliente de tu propio negocio',
    step3Title: 'Experimenta la Magia',
    step3Desc: 'Responde y descubre cómo la IA convencería a tus clientes de volver',
    howItWorks: 'Cómo funciona',
    tryFree: 'Pruébalo Gratis',
    notInterested: 'Si te convence, te ayudamos a lanzar tu primera campaña',
    noPressure: 'Sin compromiso'
  },
  en: {
    freeDemo: 'FREE DEMO',
    heroTitle: <>Try the <span className="text-amber-400">AI Reactivation Agent</span><br />on your own phone</>,
    heroSub: 'In 30 seconds you will receive an SMS from our agent. Experience how we would reactivate your inactive customers.',
    feature1Title: 'Natural Conversation',
    feature1Desc: 'AI converses like a real person, no robotic scripts',
    feature2Title: 'Immediate Results',
    feature2Desc: 'Generate sales in days, not months',
    feature3Title: 'Total Respect',
    feature3Desc: 'Agent stops immediately if customer is not interested',
    step1Title: 'Complete the Form',
    step1Desc: 'Tell us about your business and what offer you want to promote',
    step2Title: 'Receive an SMS',
    step2Desc: 'Our agent Sara will contact you as if you were a customer of your own business',
    step3Title: 'Experience the Magic',
    step3Desc: 'Reply and discover how AI would convince your customers to come back',
    howItWorks: 'How it works',
    tryFree: 'Try it Free',
    notInterested: 'If convinced, we help you launch your first campaign',
    noPressure: 'No commitment'
  },
  fr: {
    freeDemo: 'DÉMO GRATUITE',
    heroTitle: <>Essayez l'<span className="text-amber-400">Agent IA de Réactivation</span><br />sur votre propre téléphone</>,
    heroSub: 'En 30 secondes, vous recevrez un SMS de notre agent. Découvrez comment nous réactiverions vos clients inactifs.',
    feature1Title: 'Conversation Naturelle',
    feature1Desc: 'L\'IA converse comme une vraie personne, sans scripts robotiques',
    feature2Title: 'Résultats Immédiats',
    feature2Desc: 'Générez des ventes en jours, pas en mois',
    feature3Title: 'Respect Total',
    feature3Desc: 'L\'agent s\'arrête immédiatement si le client n\'est pas intéressé',
    step1Title: 'Remplissez le Formulaire',
    step1Desc: 'Parlez-nous de votre entreprise et de l\'offre que vous souhaitez promouvoir',
    step2Title: 'Recevez un SMS',
    step2Desc: 'Notre agente Sara vous contactera comme si vous étiez un client de votre propre entreprise',
    step3Title: 'Vivez la Magie',
    step3Desc: 'Répondez et découvrez comment l\'IA convaincrait vos clients de revenir',
    howItWorks: 'Comment ça marche',
    tryFree: 'Essayez Gratuitement',
    notInterested: 'Si convaincu, nous vous aidons à lancer votre première campagne',
    noPressure: 'Sans engagement'
  }
};

export const DemoAgentPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const t = PAGE_TEXTS[lang];
  const [showCalculator, setShowCalculator] = useState(true);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500 rounded-full filter blur-[120px]" />
          <div className="absolute bottom-20 right-10 w-72 h-72 bg-purple-500 rounded-full filter blur-[120px]" />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              {t.freeDemo}
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              {t.heroTitle}
            </h1>
            
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              {t.heroSub}
            </p>
          </motion.div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              {
                icon: MessageSquare,
                title: t.feature1Title,
                desc: t.feature1Desc
              },
              {
                icon: Zap,
                title: t.feature2Title,
                desc: t.feature2Desc
              },
              {
                icon: Shield,
                title: t.feature3Title,
                desc: t.feature3Desc
              }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 + idx * 0.1 }}
              >
                <feature.icon className="w-10 h-10 text-amber-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Calculator */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <ReactivationCalculator onStartDemo={() => setShowCalculator(false)} />
            </motion.div>

            {/* Demo Form */}
            <motion.div
              className="bg-white rounded-2xl p-8 shadow-2xl"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {t.tryFree}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {t.noPressure}
                  </p>
                </div>
              </div>
              
              <DemoAgentForm />
            </motion.div>
          </div>

          {/* Trust Indicators */}
          <motion.div 
            className="mt-12 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <p className="text-slate-400 text-sm mb-4">
              {lang === 'es' ? 'Tecnología utilizada por:' : lang === 'fr' ? 'Technologie utilisée par :' : 'Technology used by:'}
            </p>
            <div className="flex items-center justify-center gap-8 opacity-50">
              <span className="text-white font-semibold">{lang === 'es' ? 'Restaurantes' : lang === 'fr' ? 'Restaurants' : 'Restaurants'}</span>
              <span className="text-white font-semibold">{lang === 'es' ? 'Peluquerías' : lang === 'fr' ? 'Salons de coiffure' : 'Hair Salons'}</span>
              <span className="text-white font-semibold">{lang === 'es' ? 'Tiendas' : lang === 'fr' ? 'Boutiques' : 'Stores'}</span>
              <span className="text-white font-semibold">{lang === 'es' ? 'Servicios' : lang === 'fr' ? 'Services' : 'Services'}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            {t.howItWorks}
          </h2>
          
          <div className="space-y-8">
            {[
              {
                num: '01',
                title: t.step1Title,
                desc: t.step1Desc
              },
              {
                num: '02',
                title: t.step2Title,
                desc: t.step2Desc
              },
              {
                num: '03',
                title: t.step3Title,
                desc: t.step3Desc
              },
              {
                num: '04',
                title: lang === 'es' ? 'Decide si te interesa' : lang === 'fr' ? 'Décidez si vous êtes intéressé' : 'Decide if you are interested',
                desc: t.notInterested
              }
            ].map((step, idx) => (
              <div key={idx} className="flex gap-6 items-start">
                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-amber-600">{step.num}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
                {idx < 3 && (
                  <ArrowRight className="w-6 h-6 text-gray-300 flex-shrink-0 mt-5 hidden md:block" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DemoAgentPage;
